import { Layout, Model, TabNode, IJsonModel, Actions } from "flexlayout-react";
import { forwardRef, useCallback, useImperativeHandle, useRef, useMemo } from "react";
import { Box } from "@mantine/core";
import { WidgetContainer } from "./WidgetContainer";
import { getWidget } from "./registry";
import { generateTabId } from "./model-helpers";
import {
  LayoutActions,
  LayoutActionsContext,
  WidgetDefinition,
} from "@pulse-ui/widget-sdk";
import { addWidgetToLayout } from "./model-helpers";
import "flexlayout-react/style/light.css";
import "./flexlayout-mantine.css";

const WIDGET_DRAG_MIME_TYPE = "application/x-halo-widget";

interface WorkspaceLayoutProps {
  /** FlexLayout model (JSON or Model instance) */
  model: IJsonModel | Model;

  /** Callback when model changes */
  onModelChange?: (model: Model) => void;

  /** Custom widget registry - apps should provide their own registry */
  widgetRegistry?: Record<string, WidgetDefinition>;
}

/** Imperative API exposed via ref so a sibling (e.g. the widget library
 *  sidebar, which lives outside LayoutActionsContext) can add widgets. */
export interface WorkspaceLayoutHandle {
  openWidget: (widgetId: string) => void;
}

/**
 * Main workspace layout container using FlexLayout
 * Handles widget rendering, drag-drop, and layout persistence
 */
export const WorkspaceLayout = forwardRef<
  WorkspaceLayoutHandle,
  WorkspaceLayoutProps
>(function WorkspaceLayout(
  { model: modelProp, onModelChange, widgetRegistry },
  ref,
) {
  const layoutRef = useRef<Layout>(null);

  // Convert JSON model to Model instance if needed
  const model = useMemo(() => {
    if (modelProp instanceof Model) {
      return modelProp;
    }
    return Model.fromJson(modelProp);
  }, [modelProp]);

  const layoutActions = useMemo<LayoutActions>(
    () => ({
      openWidget: (widgetId, name, config) => {
        addWidgetToLayout(model, widgetId, name, config);
      },
    }),
    [model],
  );

  // Add a widget by id, resolving its display name/config from the registry —
  // mirrors the drop path so double-click and drag produce identical tabs.
  useImperativeHandle(
    ref,
    () => ({
      openWidget: (widgetId: string) => {
        const widget = widgetRegistry?.[widgetId] ?? getWidget(widgetId);
        if (!widget) return;
        addWidgetToLayout(model, widgetId, widget.name, {
          ...(widget.config || {}),
        });
      },
    }),
    [model, widgetRegistry],
  );

  /**
   * Factory function to render widget content for each tab
   */
  const factory = useCallback(
    (node: TabNode) => {
      const widgetId = node.getComponent();
      const instanceId = node.getId();
      const config = node.getConfig();

      if (!widgetId) {
        return <div>No component specified</div>;
      }

      // Use passed-in registry first, fall back to singleton
      const widgetDef = widgetRegistry?.[widgetId] ?? getWidget(widgetId);
      if (!widgetDef) {
        return <div>Widget not found: {widgetId}</div>;
      }

      return (
        <WidgetContainer
          widgetId={widgetId}
          instanceId={instanceId}
          WidgetComponent={widgetDef.component}
          config={config}
        />
      );
    },
    [widgetRegistry],
  );

  /**
   * Handle model changes (drag-drop, resize, etc.)
   */
  const handleModelChange = useCallback(
    (newModel: Model) => {
      if (onModelChange) {
        onModelChange(newModel);
      }
    },
    [onModelChange],
  );

  /**
   * Handle external drag (from widget library)
   */
  const handleExternalDrag = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      // Validate it's our widget
      if (!e.dataTransfer?.types.includes(WIDGET_DRAG_MIME_TYPE)) {
        return;
      }

      // Pre-assign a stable UUID in the json spec so FlexLayout uses it as
      // the node id from creation. updateNodeAttributes cannot change a node's
      // id after creation, so we must set it here.
      const tabId = generateTabId();
      return {
        json: { type: "tab", id: tabId },
        onDrop: (node?: any, event?: React.DragEvent<HTMLElement>) => {
          if (!node || !event || !event.dataTransfer) return;

          const widgetId = event.dataTransfer.getData(WIDGET_DRAG_MIME_TYPE);
          const widget = widgetRegistry?.[widgetId] ?? getWidget(widgetId);

          if (!widget) return;

          node.getModel().doAction(
            Actions.updateNodeAttributes(node.getId(), {
              name: widget.name,
              component: widgetId,
              config: { ...(widget.config || {}) },
            }),
          );
        },
      };
    },
    [widgetRegistry],
  );

  return (
    <LayoutActionsContext.Provider value={layoutActions}>
      <Box h="100%" w="100%" style={{ position: "relative" }}>
        <Layout
          ref={layoutRef}
          model={model}
          factory={factory}
          onModelChange={handleModelChange}
          onExternalDrag={handleExternalDrag}
        />
      </Box>
    </LayoutActionsContext.Provider>
  );
});
